import React from 'react'
import { flattenSpansEvents, TimelineSpan } from '../domain/TimelineSpan'
import { TraceSpanCategory } from '../domain/Trace'
import Router from '../router'
import { groupSpans } from './groupSpans'
import SpanService from './spanService'
import Span from './timeline/span'
import Summary, { SummaryLine } from './summary'
import { LayerReport } from '../domain/TraceSummary'
import { TraceDetailsLayerReport } from './tracedetails/TraceDetailsLayerReport'
import { PaginatedSpanDetails } from './tracedetails/PaginatedSpanDetails'
import { SummaryLineDetails } from './summary-line-details'
import { Box } from '../ui/box'

interface Props {
    categories: { [category: string]: TraceSpanCategory }
    spans: TimelineSpan[]
    params?: { [name: string]: string }
    router: Router
    route: string
    publicVersion: boolean
    layerReport: LayerReport
}

class SpanBasedSummary extends React.Component<Props> {
    public static defaultProps = {
        route: 'XhprofProfilerBundle.Profile.summary',
        publicVersion: false,
    }
    private spans: TimelineSpan[]
    private spanService: SpanService

    constructor(props) {
        super(props)
        this.spans = []
        this.spanService = new SpanService()
    }

    public selectSpan(spanId) {
        this.setQueryParameter({ span: spanId })
    }

    public setQueryParameter(values) {
        const params = { ...this.props.params, ...values }
        this.props.router.replace(this.props.route, params)
    }

    public render() {
        const { router, layerReport, categories } = this.props

        const maxWt = Math.max(...this.props.spans.map(span => span.wt))
        const mergedEvents = flattenSpansEvents(this.props.spans)
        const groupedSpans = groupSpans(mergedEvents, maxWt)

        const lines: SummaryLine[] = groupedSpans.map(span => ({
            name: span.name,
            callCount: Number(span.ct),
            wallTime: span.wt,
            wallTimePercentage: span.perc_wt ?? 0,
            exclusiveWallTime: span.excl_wt,
            exclusiveWallTimePercentage: span.perc_excl_wt ?? 0,
            category: this.getSpanCategory(span),
            id: span.id,
            hasDetails: span.type === 'span',
            fn: span.fn,
        }))

        this.spans = this.spanService.sort(this.props.spans)
        const selectedSpanId =
            this.props.params && this.props.params.span ? this.props.params.span : this.spans && this.spans[0] ? this.spans[0].id : null
        const spansByName = this.groupSpansByName(this.spans)
        const selectedLine = this.findSelectedLine(lines, spansByName, selectedSpanId)
        const selectedSummaryLineId = selectedLine?.id ?? null
        const relatedSpans = selectedLine ? spansByName.get(selectedLine.name) || [] : []
        const rootSpanId = this.spans[0]?.id
        const selectedRelatedSpan = this.findSelectedRelatedSpan(relatedSpans, selectedSpanId)
        return (
            <Summary
                lines={lines}
                selectedLineId={selectedSummaryLineId}
                categories={categories}
                router={router}
                onLineClick={line => this.selectSpan(line.id)}
                details={
                    this.spans && selectedLine && relatedSpans.length === 1 && selectedRelatedSpan ? (
                        <Span
                            wt={this.spans[0].wt}
                            mc={this.spans[0].mc}
                            span={selectedRelatedSpan}
                            parents={this.spanService.getParents(this.spans, selectedRelatedSpan)}
                            categories={this.props.categories}
                            route={this.props.route}
                            showSettingsLink={!this.props.publicVersion}
                            sectionBelowTitle={
                                selectedRelatedSpan.id === this.spans[0]?.id ? (
                                    <TraceDetailsLayerReport layerReport={layerReport} wt={this.spans[0].wt} />
                                ) : null
                            }
                            bottleneckRoute={
                                this.props.publicVersion
                                    ? 'XhprofCoreBundle.Public.sharedTrace.bottlenecks'
                                    : 'XhprofProfilerBundle.Profile.bottlenecksReport'
                            }
                        />
                    ) : this.spans && selectedLine && relatedSpans.length > 1 ? (
                        <div className="timeline-info">
                            <Box>
                                <SummaryLineDetails
                                    line={selectedLine}
                                    showTitle={false}
                                    sectionBelowTitle={
                                        relatedSpans.some(span => span.id === rootSpanId) ? (
                                            <TraceDetailsLayerReport layerReport={layerReport} wt={this.spans[0].wt} />
                                        ) : null
                                    }
                                />
                            </Box>
                            <PaginatedSpanDetails
                                key={selectedSummaryLineId}
                                spans={relatedSpans}
                                allSpans={this.spans}
                                spanService={this.spanService}
                                wt={this.spans[0].wt}
                                mc={this.spans[0].mc ?? 0}
                                categories={this.props.categories}
                                route={this.props.route}
                                publicVersion={this.props.publicVersion}
                                selectedSpanId={selectedSpanId || undefined}
                                onSelectedSpanChange={span => this.selectSpan(span.id)}
                            />
                        </div>
                    ) : null
                }
                isAggregated={false}
            />
        )
    }

    private getSpanCategory(span: TimelineSpan): TraceSpanCategory {
        if (span.cat in this.props.categories) {
            return this.props.categories[span.cat]
        }
        const components = span.cat.split(/\./g)
        if (components[0] in this.props.categories) {
            return this.props.categories[components[0]]
        }

        let label = span.cat
        if (span.cat === 'custom' && span.custom_type) {
            label = span.custom_type
        }

        return { className: 'php', label: `Custom [${label}]` }
    }

    private groupSpansByName(spans: TimelineSpan[]): Map<string, TimelineSpan[]> {
        return spans.reduce((groupedSpans, span) => {
            groupedSpans.set(span.name, [...(groupedSpans.get(span.name) || []), span])

            return groupedSpans
        }, new Map<string, TimelineSpan[]>())
    }

    private findSelectedLine(
        lines: SummaryLine[],
        spansByName: Map<string, TimelineSpan[]>,
        selectedSpanId: string | null,
    ): SummaryLine | null {
        if (!selectedSpanId) {
            return null
        }

        const directlySelectedLine = lines.find(line => line.id === selectedSpanId)
        if (directlySelectedLine) {
            return directlySelectedLine
        }

        return (
            // Summary rows are grouped by span name, so fall back to the row that contains the selected span.
            lines.find(line => {
                const lineSpans = spansByName.get(line.name) || []

                return lineSpans.some(span => span.id === selectedSpanId)
            }) || null
        )
    }

    private findSelectedRelatedSpan(relatedSpans: TimelineSpan[], selectedSpanId: string | null): TimelineSpan | null {
        if (!selectedSpanId) {
            return relatedSpans[0] || null
        }

        return relatedSpans.find(span => span.id === selectedSpanId) || relatedSpans[0] || null
    }
}

export default SpanBasedSummary
